/**
 * Admin Billing Page
 *
 * - Subscriptions by status, MRR and ARR from the subscriptions a payment
 *   provider is charging (comps are listed on their own and add nothing)
 * - Accounts that need chasing: past due, soft-limited, set to end
 * - A per-user plan override with a mandatory reason (audited), and the list
 *   of overrides currently in force
 * - On test stages, a payment simulator to drive a user through checkout,
 *   a failing card and the dunning calendar
 *
 * While payments run on the test provider every figure here comes from test
 * subscriptions; the page says so.
 */

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { hasRole } from "@/components/auth/AdminGuard";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import {
  useAdminBillingSummary,
  useOverridePlan,
  useSimulatePayment,
} from "@/hooks/useAdmin";
import type { BillingStatus, SimulatorEvent } from "@/services/admin";
import { getErrorMessage } from "@/services/api";

const STATUS_LABEL: Record<BillingStatus, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Past due",
  scheduled_downgrade: "Set to end or step down",
  cancelled: "Cancelled",
};

const STATUS_ORDER: BillingStatus[] = [
  "active",
  "trialing",
  "past_due",
  "scheduled_downgrade",
  "cancelled",
];

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "when", label: "Since or ends" },
  { field: "email", label: "Account", kind: "text" },
];

/** Most recent first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["plan", "stage"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "when", dir: "desc" },
};

const PLAN_FILTER_OPTIONS = [
  { value: "", label: "All plans" },
  { value: "pro", label: "Pro" },
  { value: "team", label: "Team" },
  { value: "enterprise", label: "Enterprise" },
] as const;

const STAGE_OPTIONS = [
  { value: "", label: "All stages" },
  { value: "past_due", label: "Past due" },
  { value: "override", label: "Override in force" },
] as const;

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(n);

function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
      {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

function OverrideForm({ initialUserId }: { initialUserId: string }) {
  const override = useOverridePlan();
  const [userId, setUserId] = useState(initialUserId);
  const [plan, setPlan] = useState<"pro" | "team" | "enterprise" | "none">(
    "pro"
  );
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [done, setDone] = useState<string | null>(null);

  const ready = userId.trim().length > 0 && reason.trim().length > 0;

  const submit = () => {
    setDone(null);
    override.mutate(
      {
        userId: userId.trim(),
        plan: plan === "none" ? null : plan,
        reason: reason.trim(),
        ...(until && { until: new Date(`${until}T00:00:00Z`).toISOString() }),
      },
      {
        onSuccess: (res) => {
          setDone(
            plan === "none"
              ? `Override ended; the account is now on ${res.tier}.`
              : `Account is now on ${res.tier}.`
          );
          setReason("");
        },
      }
    );
  };

  return (
    <section
      aria-label="Plan override"
      className="space-y-3 rounded-xl border border-border bg-card p-4"
    >
      <div>
        <h2 className="font-semibold">Plan override</h2>
        <p className="text-sm text-muted-foreground">
          Put an account on a plan by hand, with a reason. It counts as on that
          plan everywhere but is never counted as paying. Every change is in the
          audit log.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="override-user">User ID</Label>
          <Input
            id="override-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="01J..."
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="override-plan">Plan</Label>
          <select
            id="override-plan"
            value={plan}
            onChange={(e) => setPlan(e.target.value as typeof plan)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="pro">Pro</option>
            <option value="team">Team</option>
            <option value="enterprise">Enterprise</option>
            <option value="none">End the override (back to Free)</option>
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="override-reason">Reason</Label>
          <Input
            id="override-reason"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Launch partner, referral month, support goodwill..."
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="override-until">Ends on (optional)</Label>
          <Input
            id="override-until"
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
          />
        </div>
      </div>
      {override.isError && (
        <Alert variant="error">{getErrorMessage(override.error)}</Alert>
      )}
      {done && <Alert variant="success">{done}</Alert>}
      <Button onClick={submit} disabled={!ready} loading={override.isPending}>
        Apply override
      </Button>
    </section>
  );
}

const SIM_ACTIONS: {
  label: string;
  build: (userId: string) => SimulatorEvent;
}[] = [
  {
    label: "Make card fail",
    build: (userId) => ({ type: "card", userId, behavior: "fail" }),
  },
  {
    label: "Make card work",
    build: (userId) => ({ type: "card", userId, behavior: "ok" }),
  },
  {
    label: "Renewal comes due",
    build: (userId) => ({ type: "renew", userId }),
  },
  {
    label: "Payment fails",
    build: (userId) => ({ type: "payment.fail", userId }),
  },
  {
    label: "Payment succeeds",
    build: (userId) => ({ type: "payment.succeed", userId }),
  },
  {
    label: "Cancel at provider",
    build: (userId) => ({ type: "cancel", userId }),
  },
  {
    label: "Advance 3 days",
    build: (userId) => ({ type: "advance_days", userId, days: 3 }),
  },
];

function Simulator({ initialUserId }: { initialUserId: string }) {
  const sim = useSimulatePayment();
  const [userId, setUserId] = useState(initialUserId);
  const [last, setLast] = useState<string | null>(null);

  const run = (event: SimulatorEvent) =>
    sim.mutate(event, {
      onSuccess: (res) => setLast(JSON.stringify(res.outcome ?? null)),
    });

  return (
    <section
      aria-label="Payment simulator"
      className="space-y-3 rounded-xl border border-dashed border-border bg-card p-4"
    >
      <div>
        <h2 className="font-semibold">Payment simulator</h2>
        <p className="text-sm text-muted-foreground">
          Plays the payment company on this stage: events go through the same
          signed webhook path real ones will. Not available on production.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="sim-user">Simulator user ID</Label>
        <Input
          id="sim-user"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {SIM_ACTIONS.map((a) => (
          <Button
            key={a.label}
            size="sm"
            variant="outline"
            disabled={!userId.trim() || sim.isPending}
            onClick={() => run(a.build(userId.trim()))}
          >
            {a.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="outline"
          disabled={sim.isPending}
          onClick={() => run({ type: "run_dunning" })}
        >
          Run the daily job
        </Button>
      </div>
      {sim.isError && (
        <Alert variant="error">{getErrorMessage(sim.error)}</Alert>
      )}
      {last && (
        <pre className="overflow-x-auto rounded-md bg-muted p-2 text-xs">
          {last}
        </pre>
      )}
    </section>
  );
}

export function AdminBillingPage() {
  const { data, isLoading, error } = useAdminBillingSummary();
  const [params] = useSearchParams();
  const canChange = hasRole("platform_admin");
  const list = useListUrlState(URL_CONFIG);
  const initialUser = params.get("user") ?? "";

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <Alert variant="error">
        {getErrorMessage(error, "Failed to load billing.")}
      </Alert>
    );
  }

  const needle = list.search.toLowerCase();
  const matches = (
    account: { userId: string; email: string | null; plan: string },
    extra?: string | null
  ) =>
    (!list.filters.plan || account.plan === list.filters.plan) &&
    (!needle ||
      [account.email, account.userId, extra]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle));
  const sortAccounts = <T extends { userId: string; email: string | null }>(
    rows: T[],
    when: (row: T) => string | null
  ) =>
    sortRows(rows, list.sort, {
      when: (row) => when(row) ?? "",
      email: (row) => (row.email ?? row.userId).toLowerCase(),
    });
  const pastDueRows =
    list.filters.stage === "override"
      ? []
      : sortAccounts(
          data.pastDue.filter((p) => matches(p)),
          (p) => p.since
        );
  const compRows =
    list.filters.stage === "past_due"
      ? []
      : sortAccounts(
          data.comps.filter((c) => matches(c, c.reason)),
          (c) => c.until
        );

  const paidTotal = data.byPlan.pro + data.byPlan.team + data.byPlan.enterprise;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Billing</h1>
        <p className="text-sm text-muted-foreground">
          Subscriptions, revenue and plan overrides.
        </p>
      </header>

      {data.provider === "fake" && (
        <Alert variant="warning">
          Payments run on the test provider, so these are test subscriptions,
          not real revenue.
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="MRR"
          value={usd(data.mrrUsd)}
          note="Charging subscriptions only"
        />
        <Stat label="ARR" value={usd(data.arrUsd)} note="MRR x 12" />
        <Stat
          label="Paid plans"
          value={String(paidTotal)}
          note={`${data.byPlan.pro} Pro · ${data.byPlan.team} Team · ${data.byPlan.enterprise} Enterprise`}
        />
        <Stat
          label="Comped"
          value={String(data.comped)}
          note="On a plan, not paying"
        />
      </div>

      <section
        aria-label="Subscriptions by status"
        className="rounded-xl border border-border bg-card"
      >
        <h2 className="border-b border-border px-4 py-3 font-semibold">
          Subscriptions by status
        </h2>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border/50">
            {STATUS_ORDER.map((status) => (
              <tr key={status}>
                <td className="px-4 py-2.5">{STATUS_LABEL[status]}</td>
                <td
                  className="px-4 py-2.5 text-right tabular-nums"
                  data-testid={`status-${status}`}
                >
                  {data.byStatus[status]}
                </td>
              </tr>
            ))}
            <tr>
              <td className="px-4 py-2.5 text-muted-foreground">
                Soft-limited (of the above)
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {data.softLimited}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <AdminListToolbar
        label="Billing accounts"
        search={{
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search account, user ID or reason...",
        }}
        filters={[
          {
            id: "plan",
            label: "Show plan",
            kind: "select",
            options: PLAN_FILTER_OPTIONS,
            value: list.filters.plan!,
            onChange: (v) => list.setFilter("plan", v),
          },
          {
            id: "stage",
            label: "Stage",
            kind: "select",
            options: STAGE_OPTIONS,
            value: list.filters.stage!,
            onChange: (v) => list.setFilter("stage", v),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "server",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {pastDueRows.length > 0 && (
        <section
          aria-label="Past due"
          className="rounded-xl border border-border bg-card"
        >
          <h2 className="border-b border-border px-4 py-3 font-semibold">
            Past due
          </h2>
          <ul className="divide-y divide-border/50 text-sm">
            {pastDueRows.map((p) => (
              <li
                key={p.userId}
                className="flex items-center justify-between px-4 py-2.5"
              >
                <span>
                  <Link
                    to={`/users/${p.userId}`}
                    className="hover:text-primary hover:underline"
                  >
                    {p.email ?? p.userId}
                  </Link>{" "}
                  <Badge variant="outline" className="ml-1 capitalize">
                    {p.plan}
                  </Badge>
                </span>
                <span className="text-muted-foreground">
                  since{" "}
                  {p.since
                    ? new Date(p.since).toLocaleDateString("en-GB")
                    : "unknown"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        aria-label="Overrides in force"
        className="rounded-xl border border-border bg-card"
      >
        <h2 className="border-b border-border px-4 py-3 font-semibold">
          Overrides in force
        </h2>
        {compRows.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {list.hasActive
              ? "No overrides match your filters."
              : "No accounts are on a plan by override."}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2">Account</th>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Reason</th>
                <th className="px-4 py-2">By</th>
                <th className="px-4 py-2">Ends</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {compRows.map((c) => (
                <tr key={c.userId}>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/users/${c.userId}`}
                      className="hover:text-primary hover:underline"
                    >
                      {c.email ?? c.userId}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 capitalize">{c.plan}</td>
                  <td className="px-4 py-2.5">{c.reason ?? "-"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{c.by}</td>
                  <td className="px-4 py-2.5">
                    {c.until
                      ? new Date(c.until).toLocaleDateString("en-GB")
                      : "No end date"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {canChange ? (
        <>
          <OverrideForm initialUserId={initialUser} />
          {data.provider === "fake" && (
            <Simulator initialUserId={initialUser} />
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Changing a plan needs the platform admin role.
        </p>
      )}
    </div>
  );
}
